import { type CacheRequestName, type DegradableDependency, type FireAnalyticsEvent } from './types';

// ============================================================================
// Degraded Dependency Event
// ============================================================================

type OnDegradedDependencyArgs = {
	dependency: DegradableDependency;
	searchSessionId: string;
	reason: string;
	source: string;
	actionSubject: string;
};

/**
 * Fires an event when a dependency of search fails or times out.
 * Note: Should be fired at the request level, so the UX may not be affected due to caching.
 */
export const onDegradedDependency = ({
	dependency,
	searchSessionId,
	reason,
	source,
	actionSubject,
}: OnDegradedDependencyArgs): FireAnalyticsEvent => {
	return {
		source,
		action: 'degradedDependency',
		actionSubject,
		eventType: 'track',
		attributes: {
			additionalAttributes: {
				degradedDependency: dependency,
				reason,
				searchSessionId,
			},
		},
	};
};

// ============================================================================
// Failed Dependency Event
// ============================================================================

type OnFailedDependencyArgs = {
	dependency: DegradableDependency;
	searchSessionId: string;
	source: string;
	actionSubject: string;
};

/**
 * Fires an event when a dependency of search completely fails.
 */
export const onFailedDependency = ({
	dependency,
	searchSessionId,
	source,
	actionSubject,
}: OnFailedDependencyArgs): FireAnalyticsEvent => {
	return {
		source,
		action: 'failedDependency',
		actionSubject,
		eventType: 'operational',
		attributes: {
			additionalAttributes: {
				failedDependency: dependency,
				searchSessionId,
			},
		},
	};
};

// ============================================================================
// Cache Access Event
// ============================================================================

type OnCacheAccessArgs = {
	requestName: CacheRequestName;
	searchSessionId: string;
	cacheHit: boolean;
	refreshTriggered: boolean;
	source: string;
	actionSubject: string;
};

/**
 * Records the hit/miss rate of request caching.
 */
export const onRequestCacheAccess = ({
	requestName,
	searchSessionId,
	cacheHit,
	refreshTriggered,
	source,
	actionSubject,
}: OnCacheAccessArgs): FireAnalyticsEvent => {
	return {
		source,
		action: 'requestCacheAccess',
		actionSubject,
		eventType: 'track',
		attributes: {
			additionalAttributes: {
				requestName,
				cacheHit,
				refreshTriggered,
				searchSessionId,
			},
		},
	};
};

// ============================================================================
// Vital Stats Events (Task lifecycle tracking)
// ============================================================================

type MoreContext = Record<string, unknown>;

const baseVitalStatsEvent = ({
	searchSessionId,
	taskId,
	task,
	action,
	moreContext,
}: {
	searchSessionId: string;
	taskId: string;
	task: string;
	action: 'taskStart' | 'taskSuccess' | 'taskFail' | 'taskAbort';
	moreContext?: MoreContext;
}): FireAnalyticsEvent => {
	return {
		action,
		actionSubject: 'ui',
		eventType: 'operational',
		attributes: {
			additionalAttributes: {
				task,
				taskId,
				hostname: typeof window !== 'undefined' ? window.location.hostname : '',
				searchSessionId,
				...moreContext,
			},
		},
	};
};

type TaskEventArgs = {
	searchSessionId: string;
	taskId: string;
	/**
	 * Full task name, e.g., 'search-page/first-party-config-ux'.
	 * Use buildTaskName(prefix, CONFIG_TASK_SUFFIXES.FIRST_PARTY_CONFIG_UX) to construct.
	 */
	task: string;
	moreContext?: MoreContext;
};

/**
 * Fires when a task starts.
 */
export const onTaskStart = ({
	searchSessionId,
	taskId,
	task,
	moreContext,
}: TaskEventArgs): FireAnalyticsEvent => {
	return baseVitalStatsEvent({ searchSessionId, taskId, task, action: 'taskStart', moreContext });
};

/**
 * Fires when a task completes successfully.
 */
export const onTaskSuccess = ({
	searchSessionId,
	taskId,
	task,
	moreContext,
}: TaskEventArgs): FireAnalyticsEvent => {
	return baseVitalStatsEvent({ searchSessionId, taskId, task, action: 'taskSuccess', moreContext });
};

/**
 * Fires when a task fails.
 */
export const onTaskFail = ({
	searchSessionId,
	taskId,
	task,
	moreContext,
}: TaskEventArgs): FireAnalyticsEvent => {
	return baseVitalStatsEvent({ searchSessionId, taskId, task, action: 'taskFail', moreContext });
};

/**
 * Fires when a task is aborted (e.g., superseded or component unmounted).
 */
export const onTaskAbort = ({
	searchSessionId,
	taskId,
	task,
	moreContext,
}: TaskEventArgs): FireAnalyticsEvent => {
	return baseVitalStatsEvent({ searchSessionId, taskId, task, action: 'taskAbort', moreContext });
};
